import {
  INotificationModel,
  IUserNotificationSearchFilterModel,
  NotificationRecipientTypesService,
  NotificationsService,
  NotificationStatus,
  NotificationStatusesService,
  TimeHelper,
  useDataLoader,
} from "@xala/common-services";
import {
  Button,
  DomainTag,
  Empty,
  Icon,
  InputSearch,
  ITableColumnProps,
  ITableFilter,
  ITablePaginationConfig,
  Link,
  NotificationService,
  PageContent,
  PageHeader,
  Pagination,
  setTableColumnSearchProps,
  Table,
  IBreadcrumbProps,
  DateRangePicker,
  ITableColumnFilterDropdownProps,
  Popconfirm,
} from "@xala/common-ui";
import { generateBreadcrumb, useTableDataProvider } from "../../../../helpers";
import { ROUTES } from "./../../constants/routes";
import React, { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { FilterCleanIcon } from "../../../../resources/icons";
import moment from "moment";

const userNotificationsService = new NotificationsService().promisify();
const userNotificationsStatusService = new NotificationStatusesService().promisify();
const userNotificationsRecipienTypeService = new NotificationRecipientTypesService().promisify();
const notificationService = NotificationService.getInstance();

const notyficationType = ["USER", "SYSTEM"];

const getSortOrder = (filters: { SortBy?: string; SortOrder?: string }, column: string) => {
  if (filters.SortBy !== column) {
    return undefined;
  }
  return filters.SortOrder === "ASC" ? "ascend" : "descend";
};

const dateRangeFilter = (
  event: ITableColumnFilterDropdownProps,
  filterFrom: string | undefined,
  filterTo: string | undefined
) => {
  const { setSelectedKeys, selectedKeys, confirm, clearFilters } = event;

  const [dateRange, setDateRange] = useState<any>([
    filterFrom && moment(filterFrom),
    filterTo && moment(filterTo),
  ]);

  const filterValue: [string, string] | undefined =
    selectedKeys && selectedKeys.length > 1
      ? [selectedKeys[0]?.toString(), selectedKeys[1]?.toString()]
      : undefined;

  return (
    <div style={{ padding: 8 }}>
      <DateRangePicker
        value={dateRange}
        onChange={(values: any, dateStrings: [string, string]) => {
          setSelectedKeys([
            TimeHelper.toUTCString(dateStrings[0]),
            TimeHelper.toUTCString(`${dateStrings[1]} 23:59:59`),
          ]);
          setDateRange(values);
        }}
      />
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Button
          type="link"
          size="small"
          disabled={!filterValue}
          onClick={() => {
            if (clearFilters) {
              if (setSelectedKeys) {
                setSelectedKeys([]);
              }
              setDateRange([]);

              clearFilters();
            }
          }}
        >
          <Trans i18nKey="BUTTON_RESET">Reset</Trans>
        </Button>
        <Button
          type="primary"
          onClick={() => {
            if (confirm) {
              confirm();
            }
          }}
          size="small"
        >
          <Trans i18nKey="BUTTON_OK">OK</Trans>
        </Button>
      </div>
    </div>
  );
};

export const NotificationList = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const {
    dataLoader: notificationLoader,
    pagination,
    filters,
    fullTextSearch,
    setFullTextSearch,
  } = useTableDataProvider({
    filtersSchema: {
      Name: "string",
      Statuses: "strings",
      RecipientTypes: "strings",
      FullTextSearch: "string",
      CreatedFrom: "string",
      CreatedTo: "string",
      ScheduledOnFrom: "string",
      ScheduledOnTo: "string",
      SentOnFrom: "string",
      SentOnTo: "string",
      Types: "strings",
      Providers: "strings",
      SortOrder: "string",
      SortBy: "string",
    },
    loader: (filters, pagination) => {
      return userNotificationsService.search({
        ...filters,
        ...pagination,
      });
    },
    deps: [],
    onError: (error) =>
      notificationService.error({
        message: t(
          "LOADING_DATA_ERROR_MESSAGE",
          "There was an error while loading data."
        ),
        description: error.Message,
      }),
  });

  const type = useMemo(() => {
    if (filters.current.Providers?.find((provider) => provider === "FIREBASE")) {
      return "push";
    }

    return filters.current.Providers?.[0] || "instant";
  }, [filters.current.Providers]);

  const userNotificationRecipientTypesLoader = useDataLoader({
    loader: () =>{
      if (type === "push") {
        return userNotificationsRecipienTypeService.select("FIREBASE");
      }

      return userNotificationsRecipienTypeService.select(type);
    },
    deps: [type],
  });

  const userNotificationRecipientTypes = useMemo(
    () => userNotificationRecipientTypesLoader.data ?? [],
    [userNotificationRecipientTypesLoader.data]
  );

  const userNotificationStatusesLoader = useDataLoader({
    loader: () => userNotificationsStatusService.select(),
    deps: [],
  });

  const userNotificationTypes = useMemo(
    () => userNotificationStatusesLoader.data ?? [],
    [userNotificationStatusesLoader.data]
  );

  const getBreadcrumbProps = (): IBreadcrumbProps => {
    return generateBreadcrumb([
      {
        path: `${ROUTES.NOTIFICATION_DETAILS}`,
        breadcrumbName: t("NOTIFICATION_LIST"),
      },
    ]);
  };

  const openCreateNotification = (type: string) => {
    history.push(`${ROUTES.NOTIFICATION_CREATE}?type=${type}`);
  };

  const getColumnsProps = (): Array<ITableColumnProps<INotificationModel>> => [
    {
      key: "Name",
      dataIndex: "Name",
      title: t("MODEL_NAME", "Name"),
      width: "300px",
      filteredValue: filters.current.Name ? [filters.current.Name] : null,
      render: (_: string, row: INotificationModel) => {
        return (
          <Link to={`${ROUTES.NOTIFICATION_DETAILS}/${row.Id}`}>
            {row.NamePreview}
          </Link>
        );
      },
      ...setTableColumnSearchProps("Name"),
      sorter: true,
      sortOrder: getSortOrder(filters.current, "Name"),
    },
    {
      key: "Created",
      dataIndex: "Created",
      align: "center",
      width: "210px",
      title: t("MODEL_CREATION_DATE"),
      render: (created?: string) =>
        created ? TimeHelper.format(created) : null,
      filteredValue:
        filters.current.CreatedFrom !== undefined &&
        filters.current.CreatedTo !== undefined
          ? [filters.current.CreatedFrom, filters.current.CreatedTo]
          : undefined,
      filterDropdown: (event: ITableColumnFilterDropdownProps) =>
        dateRangeFilter(
          event,
          filters.current.CreatedFrom,
          filters.current.CreatedTo
        ),
      sorter: true,
      sortOrder: getSortOrder(filters.current, "Created"),
    },
    {
      key: "ScheduledOn",
      dataIndex: "ScheduledOn",
      align: "center",
      width: "210px",
      title: t("NOTIFICATION_SCHEDULEDON_FIELD", "Scheduled On"),
      render: (created?: string) =>
        created ? TimeHelper.format(created) : "-",
      filteredValue:
        filters.current.ScheduledOnFrom !== undefined &&
        filters.current.ScheduledOnTo !== undefined
          ? [filters.current.ScheduledOnFrom, filters.current.ScheduledOnTo]
          : undefined,
      filterDropdown: (event: ITableColumnFilterDropdownProps) =>
        dateRangeFilter(
          event,
          filters.current.ScheduledOnFrom,
          filters.current.ScheduledOnTo
        ),
      sorter: true,
      sortOrder: getSortOrder(filters.current, "ScheduledOn"),
    },
    ...( type === "push" ? [{
      key: "SentOn",
      dataIndex: "SentOn",
      title: t("MODEL_SENT_ON", "Sent On"),
      align: "center" as const,
      width: "220px",
      render: (sentOn?: string) =>
        sentOn ? TimeHelper.format(sentOn) : "-",
      filteredValue:
        filters.current.SentOnFrom !== undefined &&
        filters.current.SentOnTo !== undefined
          ? [filters.current.SentOnFrom, filters.current.SentOnTo]
          : undefined,
      filterDropdown: (event: ITableColumnFilterDropdownProps) =>{;
        return dateRangeFilter(
          event,
          filters.current.SentOnFrom,
          filters.current.SentOnTo
        )
      },
    }] : [{
      key: "Type",
      dataIndex: "Type",
      title: t("MODEL_TYPE", "Type"),
      align: "center" as const,
      width: "100px",
      filteredValue: filters.asTableArray.Types,
      filters: notyficationType.map((type) => ({
        text: type,
        value: type,
      })),
    }]),
    {
      key: "CreatorUserFullName",
      dataIndex: "CreatorUserFullName",
      title: t("MODEL_CREATED_BY", "Created by"),
      align: "center",
      width: "200px",
      sorter: true,
      sortOrder: getSortOrder(filters.current, "CreatorUserFullName"),
    },
    {
      key: "RecipientTypes",
      dataIndex: "RecipientType",
      title: t("MODEL_RECIPIENT_TYPE", "Recipient"),
      align: "center",
      width: "200px",
      filters: userNotificationRecipientTypes.map(
        (userNotificationRecipientType) => ({
          text: userNotificationRecipientType.DisplayName,
          value: userNotificationRecipientType.Code,
        })
      ),
      render: (_: string, row) => (
        <DomainTag
          key={row.RecipientType}
          domain="notification-recipients"
          noMargin
          colorRotate={row.RecipientType}
          value={
            userNotificationRecipientTypes.find(
              (it) => it.Code === row.RecipientType
            )?.DisplayName || row.RecipientType
          }
        />
      ),
      filteredValue: filters.asTableArray.RecipientTypes,
    },
    {
      key: "Statuses",
      dataIndex: "Status",
      title: t("MODEL_STATUS", "Status"),
      align: "center",
      width: "250px",
      filters: userNotificationTypes.map((userNotificationType) => ({
        text: userNotificationType.DisplayName,
        value: userNotificationType.Code,
      })),
      filteredValue: filters.asTableArray.Statuses,
      render: (_: string, row) => (
        <DomainTag
          key={row.Status}
          domain="notification-status"
          noMargin
          colorRotate={row.Status}
          value={
            userNotificationTypes.find((it) => it.Code === row.Status)
              ?.DisplayName || row.Status
          }
        />
      ),
    },
    {
      key: "Actions",
      dataIndex: "Actions",
      align: "center",
      width: "100px",
      title: t("TABLE_ACTIONS_COLUMN"),
      render: (_, data: INotificationModel) => (
        <div className="asset-table__actions">
          {type === "push" && <Button
            icon={<Icon type="Copy" />}
            title={t("DUPLICATE_ELEMENT")}
            onClick={
              async () => {
                const result = await userNotificationsService.duplicate(data.Id)
                if (result.ok) {
                  notificationLoader.refresh();
                } else {
                  notificationService.error({
                    message: t("DUPLICATE_ELEMENT_FAILTURE"),
                    description: result.error?.Message,
                  });
                }
              }
            }
          />}
          <Popconfirm
            title={t("DELETE_ELEMENT_DOUBLE_CONFIRMATION_QUESTION")}
            onConfirm={async (e?: React.MouseEvent<HTMLElement>) => {
              e?.preventDefault();
              const result = await userNotificationsService.delete(data);
              if (result.ok) {
                notificationLoader.refresh();
              } else {
                notificationService.error({
                  message: t("DELETE_ELEMENT_FAILTURE"),
                  description: result.error?.Message,
                });
              }
            }}
            okText={t("BUTTON_YES")}
            cancelText={t("BUTTON_NO")}
          >
            <Button
              danger={true}
              icon={<Icon type="delete" />}
              title={t("DELETE_ELEMENT")}
              disabled={
                data?.Status === NotificationStatus.Sent ||
                data?.Status === NotificationStatus.InProgress ||
                data?.Status === NotificationStatus.Scheduled
              }
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleTableChange = (
    _: ITablePaginationConfig,
    tableFilters: ITableFilter,
    sorter: any
  ) => {
    const sortOrder = sorter.order ? sorter.order === "ascend" ?"ASC" : "DESC" : undefined;
    const sortBy = sorter.column?.key ? sorter.column.key as "Name" | "Created" | "ScheduledOn" | "SentOn" | "Type" | "CreatorUserFullName" | "RecipientType" | "Status" : undefined;

    const newFilters: IUserNotificationSearchFilterModel = {
      RecipientTypes: (tableFilters.RecipientTypes || []) as string[],
      Statuses: (tableFilters.Statuses || []) as string[],
      Name: tableFilters.Name?.[0].toString(),
      CreatedFrom: tableFilters.Created?.[0]?.toString(),
      CreatedTo: tableFilters.Created?.[1]?.toString(),
      ScheduledOnFrom: tableFilters.ScheduledOn?.[0]?.toString(),
      ScheduledOnTo: tableFilters.ScheduledOn?.[1]?.toString(),
      SentOnFrom: tableFilters.SentOn?.[0]?.toString(),
      SentOnTo: tableFilters.SentOn?.[1]?.toString(),
      Types: (tableFilters.Type || []) as string[],
      SortOrder: sortOrder,
      SortBy: sortBy,
    };
    filters.update((oldFilters) => ({
      ...oldFilters,
      ...newFilters,
    }));
  };

  const onSearch = (value: string) =>
    filters.update((oldFilters) => ({
      ...oldFilters,
      FullTextSearch: value || undefined,
    }));

  const columns = getColumnsProps();

  const getExtraButtons = (
    <>
      <InputSearch
        key="search"
        placeholder="Search"
        value={fullTextSearch}
        onChange={({ target: { value } }) => setFullTextSearch(value)}
        onSearch={onSearch}
        style={{ width: 250 }}
        allowClear
      />
      <Button
        shape="circle"
        disabled={!filters.anyActive}
        icon={<FilterCleanIcon />}
        onClick={filters.clear}
        title={t("MENU_OPTION_CLEAR_FILTERS")}
      />
      <Button
        shape="circle"
        icon={<Icon type="reload" />}
        title={t("BUTTON_REFRESH_TITLE")}
        onClick={notificationLoader.refresh}
      />
      <Button
        onClick={() => openCreateNotification(filters.current.Providers?.[0] || "FIREBASE")}
        title={t("NOTIFICATION_ADD")}
        shape="circle"
        type="primary"
        icon={<Icon type="plus" />}
      />
    </>
  );

  return (
    <PageContent footer={<Pagination {...pagination.props} />}>
      <PageHeader
        title={t(`NOTIFICATION_LIST_${type.toUpperCase()}`)}
        breadcrumb={getBreadcrumbProps()}
        extra={getExtraButtons}
      />
      <Table<INotificationModel>
        locale={{
          emptyText: (
            <>
              <Empty />
              {filters.anyActive && (
                <Button icon={<FilterCleanIcon />} onClick={filters.clear}>
                  {t("MENU_OPTION_CLEAR_FILTERS")}
                </Button>
              )}
            </>
          ),
        }}
        rowKey="Id"
        columns={columns}
        dataSource={notificationLoader.data?.Entities || []}
        loading={notificationLoader.loading}
        pagination={false}
        onChange={handleTableChange}
      />
    </PageContent>
  );
};
